// Pure helpers for the profile page (docs/profile-rehaul-plan.md).
import { HOURS, IDEA_STAGES, INDUSTRIES, STARTUP_STAGES, labelOf } from "../onboarding/config"

// ---- covers ----

export const COVER_TONES = [
  { id: "sand", name: "Sand", color: "#F3E8D8" },
  { id: "sage", name: "Sage", color: "#DCE8DA" },
  { id: "sky", name: "Sky", color: "#DCE9FB" },
  { id: "clay", name: "Clay", color: "#F4D9CC" },
  { id: "butter", name: "Butter", color: "#F8EDC2" },
  { id: "stone", name: "Stone", color: "#E7E5E4" },
]
export const COVER_PATTERNS = [
  { id: "arches", name: "Arches", base: "#DCE8DA" },
  { id: "hills", name: "Hills", base: "#F3E8D8" },
  { id: "drift", name: "Drift", base: "#DCE9FB" },
]

// a tone picked from the user id, so default pages don't all look the same
export const defaultCoverStyle = (userId) => {
  let h = 0
  String(userId || "").split("").forEach((c) => {
    h = (h * 31 + c.charCodeAt(0)) >>> 0
  })
  return `tone:${COVER_TONES[h % COVER_TONES.length].id}`
}

// { kind: "tone" | "pattern" | "photo", id, color, photo, name }
export const resolveCover = (user) => {
  let style = user.coverStyle
  if (style === "photo" && !user.coverPhoto) style = null
  if (style === "photo") return { kind: "photo", photo: user.coverPhoto, name: "Your photo", style }
  const [kind, id] = String(style || "").split(":")
  const pattern = kind === "pattern" && COVER_PATTERNS.find((p) => p.id === id)
  if (pattern) return { kind: "pattern", id, color: pattern.base, name: pattern.name, style }
  const tone =
    (kind === "tone" && COVER_TONES.find((t) => t.id === id)) ||
    COVER_TONES.find((t) => `tone:${t.id}` === defaultCoverStyle(user.id))
  return { kind: "tone", id: tone.id, color: tone.color, name: tone.name, style: `tone:${tone.id}` }
}

// ---- prompts (ids match backend/src/services/profile-sections.js) ----

export const PROMPTS = [
  { id: "cofounder_for_you", label: "I'm the co-founder for you if…" },
  { id: "proud_build", label: "Something I built that I'm proud of" },
  { id: "unfair_advantage", label: "My unfair advantage" },
  { id: "bad_at", label: "What I'm bad at, so you don't have to be" },
  { id: "cant_stop", label: "The problem I can't stop thinking about" },
  { id: "how_i_work", label: "How I like to work" },
  { id: "opinion", label: "A startup opinion I'd defend" },
  { id: "year_from_now", label: "A year from now I want to…" },
]
export const promptLabel = (id) => (PROMPTS.find((p) => p.id === id) || {}).label
export const MAX_PROMPTS = 3
export const MAX_PROMPT_ANSWER = 200
export const MAX_LINKS = 5
export const MAX_HEADLINE = 100

export const parseJsonList = (text) => {
  if (!text) return []
  try {
    const value = JSON.parse(text)
    return Array.isArray(value) ? value : []
  } catch (e) {
    return []
  }
}
export const promptsOf = (user) =>
  parseJsonList(user.profilePromptsJson).filter((p) => p && promptLabel(p.id) && p.answer)
export const linksOf = (user) => parseJsonList(user.profileLinksJson).filter((l) => l && l.url)

// ---- text ----

// the old sign-up's pre-filled line counts as empty
const STOCK_TEXT_START = "i am still working out the details to fill out this section"
export const isStockText = (text) =>
  typeof text === "string" && text.replace(/\s+/g, " ").trim().toLowerCase().startsWith(STOCK_TEXT_START)
export const realText = (text) => (typeof text === "string" && !isStockText(text) ? text.trim() : "")

// Stored text has "<br />" from the old editor and newlines from the new one.
// Returns paragraphs, each a list of lines.
// A line that's only punctuation ("." was a common spacer in the old
// editor) counts as a paragraph break.
export const paragraphs = (text) =>
  realText(text)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\r\n?/g, "\n")
    .replace(/^[ \t]*[.\-–—_*·•~]+[ \t]*$/gm, "")
    .split(/\n\s*\n/)
    .map((p) =>
      p
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
    )
    .filter((lines) => lines.length)

export const wordCount = (text) => (realText(text).match(/\S+/g) || []).length

// for the editors: old "<br />" back to newlines
export const editableText = (text) => realText(text).replace(/<br\s*\/?>/gi, "\n")

// Names typed ALL CAPS or all lowercase, title-cased for display.
export const displayName = (name) => {
  const n = String(name || "").trim()
  if (n.length < 2) return n.toUpperCase()
  if (n !== n.toUpperCase() && n !== n.toLowerCase()) return n
  return n
    .toLowerCase()
    .split(/([\s\-'’]+)/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("")
}

// ---- links ----

const KNOWN_SITES = [
  [/(^|\.)linkedin\.com$/, "LinkedIn"],
  [/(^|\.)github\.com$/, "GitHub"],
  [/(^|\.)(twitter|x)\.com$/, "X"],
  [/(^|\.)youtube\.com$|(^|\.)youtu\.be$/, "YouTube"],
  [/(^|\.)instagram\.com$/, "Instagram"],
  [/(^|\.)medium\.com$/, "Medium"],
  [/(^|\.)substack\.com$/, "Substack"],
  [/(^|\.)dribbble\.com$/, "Dribbble"],
  [/(^|\.)behance\.net$/, "Behance"],
  [/(^|\.)producthunt\.com$/, "Product Hunt"],
  [/(^|\.)calendly\.com$/, "Calendly"],
  [/(^|\.)angel\.co$|(^|\.)wellfound\.com$/, "Wellfound"],
]
export const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "")
  } catch (e) {
    return String(url || "").replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0]
  }
}
export const linkLabel = (url) => {
  const host = hostOf(url)
  const known = KNOWN_SITES.find(([re]) => re.test(host))
  return known ? known[1] : host
}

// ---- skills, intents, labels ----

const SKILL_WORDS = {
  1: { noun: "Developer", plural: "developers", want: "a developer", is: "is a developer" },
  2: { noun: "Designer", plural: "designers", want: "a designer", is: "is a designer" },
  3: { noun: "Marketer", plural: "marketers", want: "a marketer", is: "is a marketer" },
  4: {
    noun: "Business developer",
    plural: "business developers",
    want: "someone for business development",
    is: "does business development",
  },
  5: { noun: "Salesperson", plural: "salespeople", want: "someone for sales", is: "does sales" },
  6: { noun: "Product person", plural: "product people", want: "a product person", is: "is a product person" },
}
export const skillWords = (itemId) => SKILL_WORDS[itemId] || null
const skillIds = (list) => (list || []).map((s) => s.itemId).filter((id) => SKILL_WORDS[id])

export const SKILL_LABELS = {
  1: "Developer",
  2: "Designer",
  3: "Marketer",
  4: "Business development",
  5: "Sales",
  6: "Product",
}
export const hoursLabel = (h) => {
  const label = h && labelOf(HOURS, h.itemId)
  return label ? `${label} a week` : null
}
export const ideaStageLabel = (s) => (s && labelOf(IDEA_STAGES, s.itemId)) || null
export const startupStageLabel = (s) => (s && labelOf(STARTUP_STAGES, s.itemId)) || null
export const industryLabel = (i) => (i && labelOf(INDUSTRIES, i.itemId)) || null

// What they're here for, in their own voice for the owner and about them for visitors
export const intentLines = (user, { own } = {}) =>
  [
    user.hasIdeaForStartup && (own ? "Find a co-founder for your idea" : "Find a co-founder for an idea"),
    user.lookingToJoinStartupAsFounder && "Join someone else's idea",
    user.hasOwnStartup && (own ? "Grow the startup you run" : "Grow the startup they run"),
    user.interestedInJobsForStartups && "Hear about startup jobs",
  ].filter(Boolean)

// A headline written from their answers, until they write their own:
// "Developer and product person. Wants to join an idea as a co-founder."
export const generatedHeadline = (user) => {
  const nouns = skillIds(user.hasSkills).map((id) => SKILL_WORDS[id].noun)
  const who = nouns.length ? `${nouns[0]}${nouns[1] ? ` and ${nouns[1].toLowerCase()}` : ""}.` : ""
  let what = ""
  if (user.hasIdeaForStartup && user.lookingToJoinStartupAsFounder) what = "Has an idea and is open to joining one."
  else if (user.hasIdeaForStartup) what = "Has an idea and needs a co-founder."
  else if (user.lookingToJoinStartupAsFounder) what = "Wants to join an idea as a co-founder."
  else if (user.hasOwnStartup) what = "Runs a startup."
  else if (user.interestedInJobsForStartups) what = "Open to startup jobs."
  return [who, what].filter(Boolean).join(" ") || null
}

// ---- time ----

const zoneParts = (timeZone, date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  }).formatToParts(date)
  const get = (type) => Number((parts.find((p) => p.type === type) || {}).value)
  return Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"))
}
// minutes ahead of UTC right now
export const zoneOffset = (timeZone, date = new Date()) => {
  try {
    return Math.round((zoneParts(timeZone, date) - Math.floor(date.getTime() / 60000) * 60000) / 60000)
  } catch (e) {
    return null
  }
}
export const localTime = (timeZone, date = new Date()) => {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" })
      .format(date)
      .replace("AM", "am")
      .replace("PM", "pm")
  } catch (e) {
    return null
  }
}
export const cityOf = (location) => String(location || "").split(",")[0].trim()

// ---- "You and {name}" ----

// Up to three reasons, from both profiles. Always the first name, never a pronoun.
export const fitLines = (me, user, now = new Date()) => {
  if (!me || !user || me.id === user.id) return []
  const name = displayName(user.firstName)
  const lines = []
  const theirNeeds = skillIds(user.wantsSkills)
  const myNeeds = skillIds(me.wantsSkills)
  const mine = skillIds(me.hasSkills)
  const theirs = skillIds(user.hasSkills)
  const youBring = theirNeeds.find((id) => mine.includes(id))
  if (youBring) lines.push(`${name} is looking for ${SKILL_WORDS[youBring].want}. That's what you bring.`)
  const theyBring = myNeeds.find((id) => theirs.includes(id))
  if (theyBring) lines.push(`${name} ${SKILL_WORDS[theyBring].is}. That's who you're looking for.`)
  if (user.lookingToJoinStartupAsFounder && me.hasIdeaForStartup) {
    lines.push(`${name} wants to join an idea. You have one.`)
  } else if (user.hasIdeaForStartup && me.lookingToJoinStartupAsFounder) {
    lines.push(`${name} has an idea and needs a co-founder. You want to join one.`)
  }
  const sameHours =
    user.hoursPerWeekAvailable && me.hoursPerWeekAvailable && user.hoursPerWeekAvailable.itemId === me.hoursPerWeekAvailable.itemId
  if (sameHours) {
    const label = labelOf(HOURS, user.hoursPerWeekAvailable.itemId)
    if (label) lines.push(`You can both give ${label.replace("hrs", "hours")} a week.`)
  }
  if (user.locationTimezone && me.locationTimezone) {
    const theirs = zoneOffset(user.locationTimezone, now)
    const yours = zoneOffset(me.locationTimezone, now)
    const there = cityOf(user.location)
    const here = cityOf(me.location)
    if (theirs != null && yours != null && there && here) {
      const diff = Math.round((theirs - yours) / 60)
      if (diff === 0) lines.push(there === here ? `You're both in ${there}.` : `Same time zone: ${there} and ${here}.`)
      else {
        const n = Math.abs(diff)
        lines.push(`${there} is ${n} hour${n === 1 ? "" : "s"} ${diff > 0 ? "ahead of" : "behind"} ${here}.`)
      }
    }
  }
  return lines.slice(0, 3)
}

// ---- the owner's checklist ----

export const checklist = (me) => {
  const ideaWithoutName =
    me.hasIdeaForStartup && realText(me.elevatorPitchForStartup) && !(me.ideas || []).length
  return [
    { key: "photo", label: "Photo", done: Boolean(me.profilePic) },
    {
      key: "skills",
      label: "What you bring and need",
      done: Boolean((me.hasSkills || []).length && (me.wantsSkills || []).length),
    },
    { key: "where", label: "Time and place", done: Boolean(me.location && me.hoursPerWeekAvailable) },
    { key: "about", label: "About you", done: Boolean(realText(me.whyMakeGoodCofounder)) },
    { key: "headline", label: "A headline in your words", done: Boolean(me.headline) },
    ideaWithoutName ? { key: "idea", label: "A name for your idea", done: false } : null,
    { key: "prompts", label: "One prompt", done: promptsOf(me).length > 0 },
    { key: "links", label: "Your links", done: linksOf(me).length > 0 },
  ].filter(Boolean)
}

export const profileUrl = (id) => `https://www.foundercloud.com/profile/${id}`

// Splits text into strings and { url, text } links for http(s):// and www.
// addresses, leaving trailing punctuation outside the link.
export const linkify = (text) => {
  const out = []
  const re = /\b(?:https?:\/\/|www\.)[^\s<>"]+/gi
  let last = 0
  let m
  while ((m = re.exec(text))) {
    let raw = m[0]
    const trail = (raw.match(/[.,;:!?)\]'"]+$/) || [""])[0]
    raw = raw.slice(0, raw.length - trail.length)
    if (m.index > last) out.push(text.slice(last, m.index))
    out.push({ url: /^https?:/i.test(raw) ? raw : `https://${raw}`, text: raw.replace(/^https?:\/\//i, "") })
    last = m.index + raw.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}
